/**
 * ClientNetWorld - NetWorld implementation for connected game clients.
 * Receives replication packets, routes RPCs, sends heartbeats. Server is authoritative.
 */
import { SceneComponent } from '../components/SceneComponent.js';
import { createLoader } from '../utils/serialization/factories.js';

import { createLagSimulatingConnectionFactory } from './implementations/LagSimulatingNetworkTransport.js';
import { NetworkGameSession, WebSocketNetworkConnection } from './implementations/NetworkGameSession.js';
import {
  type ActorAck,
  type ClientId,
  type IncomingRpc,
  type ObjectId,
  type ReplicationAck,
  type ReplicationPacket
} from './interface.js';
import { getDeviceId } from './NetIdentity.js';
import { type NetClientConnectOptions, NetWorld } from './NetWorld.js';
import { BinaryReader } from './ReplicationBinaryCodec.js';
import { ReplicationSerializer } from './ReplicationSerializer.js';
import { REPLICATION_COMPONENTS_KEY } from './ReplicationSerializer.js';
import { decodeRpcArgs, encodeRpcArgs } from './RpcBinaryCodec.js';

import type { Actor } from '../actors/Actor.js';
import type { NetPropertyMetadata } from '../utils/serialization/decorator.js';
import type { SerializedData } from '../utils/serialization/types.js';


/**
 * `NetWorld` subclass for player-facing client instances connected to a remote server.
 */
export class ClientNetWorld extends NetWorld<NetworkGameSession> {
  /** Transport ID → Actor mapping for incoming replication packets. */
  protected transportIdToActor: Map<number, Actor> = new Map();


  // ---------------------------------------------------------------------------
  // Dangling actor-reference tracking (two-index structure)
  //
  // danglingForward:  ownerUuid → (dotPath → referencedUuid)
  //   Used for O(1) cleanup when an owner actor is destroyed, and to detect
  //   superseded references (same dotPath updated to point at a different actor).
  //
  // danglingInverted: referencedUuid → (ownerUuid → Set<dotPath>)
  //   Used for O(1) lookup when a new actor spawns — only the entries that
  //   reference the newly spawned UUID need to be checked.
  // ---------------------------------------------------------------------------

  private danglingForward: Map<string, Map<string, string>> = new Map();
  private danglingInverted: Map<string, Map<string, Set<string>>> = new Map();

  /**
   * Builds a {@link NetworkGameSession}, awaits handshake readiness, and attaches it to this world.
   */
  public async connectToServer(options: NetClientConnectOptions): Promise<void> {
    const lagSimulationConfig = options.lagSimulationConfig;
    const session = new NetworkGameSession({
      serverUrl: options.serverUrl,
      connectionFactory: lagSimulationConfig?.enabled
        ? createLagSimulatingConnectionFactory((url) => new WebSocketNetworkConnection(url), lagSimulationConfig)
        : undefined
    });
    await session.waitUntilReady();
    await this.attachGameSession(session);
  }

  /**
   * Clients are passive — replication is event-driven via `handleReplicationPacket`,
   * so there is nothing to send here. We still need to refresh the network stats
   * overlay every tick.
   * @param _deltaTime - Unused.
   */
  public override async tick(_deltaTime: number): Promise<void> {
    this.updateNetStats(0);
  }

  /**
   * Wires up client-side session handlers once a session has been assigned.
   * Hooks: session lifecycle, incoming RPCs, incoming replication packets,
   * console commands, and a periodic heartbeat.
   */
  protected override async onGameSessionSet(): Promise<void> {
    this.flushPendingNetObjects();

    const session = this.gameSession!;

    session.onPlayerJoined.add(this.onSessionPlayerJoined.bind(this));
    session.onPlayerLeft.add(this.onSessionPlayerLeft.bind(this));
    session.onSessionStateChanged.add(this.onSessionStateChanged.bind(this));
    session.onRPCReceived.add(this.handleIncomingRpc.bind(this));
    session.onReplicationPacketReceived.add(this.handleReplicationPacket.bind(this));

    // Make this instance available for console commands (e.g. disconnect, stats).
    this.world.consoleManager.addCommandHandler(this);

    // Initial heartbeat so the server registers this client immediately.
    await this.sendHeartbeat();

    // Periodic heartbeat to keep the connection alive.
    this.startHeartbeatTimer();
  }

  /**
   * Send RPC from client to server.
   * Resolves param metadata, encodes args to binary, then hands off to the session.
   */
  public override sendServerRPC(
    objectId: ObjectId,
    methodName: string,
    args: unknown[],
    reliable: boolean,
    netSubId?: string
  ): void {
    if (!this.gameSession) {
      console.warn('[ClientNetWorld] Cannot send server RPC: no game session');
      return;
    }

    const actor = this.getLocalActorByObjectId(objectId);
    let params: NetPropertyMetadata[] | null;
    if (netSubId && actor) {
      const component = actor.getComponents(SceneComponent).find(c => c.uuid === netSubId);
      params = component ? this.getRpcParamsForMethod(component.constructor, methodName) : null;
    } else {
      params = actor ? this.getRpcParamsForMethod(actor.constructor, methodName) : null;
    }

    if (!params) {
      console.error(
        `[ClientNetWorld] RPC "${methodName}" has no param metadata. ` +
        'Add params to the RPC decorator or ensure the property metadata generator has run.',
      );
      return;
    }

    const argsPayload = encodeRpcArgs(args, params);
    this.gameSession.sendServerRpc(objectId, methodName, argsPayload, reliable, netSubId);
    this.netStats.numOutgoingRPCs++;
  }

  /**
   * Decode binary RPC payload and dispatch to the target actor or component.
   * Subscribed to `session.onRPCReceived` in {@link onGameSessionSet}.
   */
  private handleIncomingRpc(message: IncomingRpc, senderId?: ClientId): void {
    const { objectId, methodName, argsPayload, netSubId } = message;

    const actor = this.getLocalActorByObjectId(objectId);
    if (!actor) {
      console.warn(`[ClientNetWorld] RPC target actor not found: ${objectId}`);
      return;
    }

    let params: NetPropertyMetadata[] | null;
    if (netSubId) {
      const component = actor.getComponents(SceneComponent).find(c => c.uuid === netSubId);
      if (!component) {
        console.warn(`[ClientNetWorld] RPC component "${netSubId}" not found on actor ${objectId}`);
        return;
      }
      params = this.getRpcParamsForMethod(component.constructor, methodName);
    } else {
      params = this.getRpcParamsForMethod(actor.constructor, methodName);
    }

    if (!params) {
      console.error(
        `[ClientNetWorld] RPC "${methodName}" has no param metadata — cannot decode. Dropping.`,
      );
      return;
    }

    let args: unknown[];
    try {
      args = decodeRpcArgs(new BinaryReader(argsPayload), params);
    } catch (error) {
      console.error(`[ClientNetWorld] Failed to decode RPC "${methodName}":`, error);
      return;
    }

    this.dispatchRPCCall(actor, methodName, args, netSubId, senderId);
  }

  /**
   * Handle per-actor replication packet from server.
   * Spawns, updates, or destroys actors based on transport-ID-keyed updates,
   * resolving dangling actor references when new actors arrive.
   */
  protected async handleReplicationPacket(packet: ReplicationPacket): Promise<void> {
    const acksToSend: ActorAck[] = [];

    for (const actorUpdate of packet.actors) {
      const { transportId, updateType } = actorUpdate;

      if (updateType === 'spawn') {
        const spawnData = actorUpdate.data;
        try {
          const constructionData: SerializedData | undefined = spawnData.constructionData;

          if (!constructionData) {
            console.error('[ClientNetWorld] Missing construction data in spawn packet');
            continue;
          }

          let constructedActor: Actor;
          try {
            constructedActor = await createLoader().loadAsync(constructionData) as Actor;
          } catch (error) {
            console.error('[ClientNetWorld] Loader failed to construct actor:', error);
            continue;
          }

          if (!constructedActor) {
            console.error('[ClientNetWorld] Failed to construct actor from spawn data');
            continue;
          }

          // Override UUID with the one from server (important for matching scene-placed actors)
          if (spawnData.uuid) {
            (constructedActor as any).uuid = spawnData.uuid;
          }

          // Check if an actor with this UUID already exists (e.g., loaded from scene file)
          const existingActor = this.world.getActors().find(a => a.uuid === constructedActor.uuid);
          if (existingActor) {
            console.log(`[ClientNetWorld] Replacing existing actor ${existingActor.constructor.name} (${existingActor.uuid}) with server version`);

            this.world.removeActors(existingActor);

            const existingNetId = this.getNetId(existingActor);
            if (existingNetId) {
              this.localNetObjects.delete(existingNetId);
            }
          }

          // Set network properties
          constructedActor.netOwningClientId = spawnData.owningClientId ?? null;
          constructedActor.setRemoteNetClientId(spawnData.owningClientId ?? '');
          constructedActor.setRemoteNetDeviceId(getDeviceId());

          // Apply initial binary state.
          // Server has already swapped the roles before sending, so we just apply them directly.
          if (spawnData.initialState) {
            const unresolvedRefs = ReplicationSerializer.deserializeIntoActor(
              constructedActor,
              spawnData.initialState,
              (uuid) => this.world.getActorByUuid(uuid)
            );

            // Track any dangling actor references.
            if (unresolvedRefs.size > 0) {
              for (const [dotPath, referencedUuid] of unresolvedRefs) {
                this.addDanglingRef(constructedActor.uuid, dotPath, referencedUuid);
              }
              console.debug(`[ClientNetWorld] Actor ${constructedActor.constructor.name} has ${unresolvedRefs.size} unresolved references:`, [...unresolvedRefs.keys()]);
            }
          }

          this.world.addActors(constructedActor);

          this.transportIdToActor.set(transportId, constructedActor);

          acksToSend.push({
            transportId,
            ackType: 'spawned'
          });

          // Resolve any existing actors that were waiting for this newly spawned actor.
          this.resolveDanglingReferences(constructedActor.uuid);
        } catch (error) {
          console.error('[ClientNetWorld] Failed to spawn actor from replication packet:', error);
        }
      } else if (updateType === 'update') {
        const actor = this.transportIdToActor.get(transportId);
        if (actor) {
          const updateData = actorUpdate.data;

          const unresolvedRefs = ReplicationSerializer.deserializeIntoActor(
            actor,
            updateData.payload,
            (uuid) => this.world.getActorByUuid(uuid)
          );

          if (unresolvedRefs.size > 0) {
            for (const [dotPath, referencedUuid] of unresolvedRefs) {
              this.addDanglingRef(actor.uuid, dotPath, referencedUuid);
            }
            console.debug(`[ClientNetWorld] Actor ${actor.constructor.name} has ${unresolvedRefs.size} unresolved references:`, [...unresolvedRefs.keys()]);
          }

          // No ack for updates - property replication is unreliable by default
        } else {
          console.warn(`[ClientNetWorld] Received update for unknown transport ID: ${transportId}`);
        }
      } else if (updateType === 'destroy') {
        const actor = this.transportIdToActor.get(transportId);
        if (actor) {
          this.removeDanglingRefsForOwner(actor.uuid);
          this.world.removeActors(actor);
          this.transportIdToActor.delete(transportId);
        }
      }
    }

    // Send acknowledgments back to server
    if (acksToSend.length > 0) {
      console.log(`[ClientNetWorld] Sending ${acksToSend.length} acks to server:`, acksToSend.map(a => `${a.ackType} transport=${a.transportId}`));
      const ack: ReplicationAck = {
        tick: packet.tick,
        actors: acksToSend
      };

      if (this.gameSession) {
        this.gameSession.sendReplicationAck(ack);
      } else {
        console.warn('[ClientNetWorld] No game session attached; cannot send replication ack');
      }
    }
  }

  /**
   * Register an unresolved actor reference so it can be resolved when the target spawns.
   * If the same `(ownerUuid, dotPath)` pair already points at a different referenced UUID
   * (superseded reference), the old entry is removed from the inverted index first.
   */
  private addDanglingRef(ownerUuid: string, dotPath: string, referencedUuid: string): void {
    // Forward map: ownerUuid → (dotPath → referencedUuid)
    let pathMap = this.danglingForward.get(ownerUuid);
    if (!pathMap) {
      pathMap = new Map();
      this.danglingForward.set(ownerUuid, pathMap);
    }
    const oldRefUuid = pathMap.get(dotPath);
    if (oldRefUuid && oldRefUuid !== referencedUuid) {
      // Superseded reference — remove old entry from inverted index.
      this.removeFromInverted(oldRefUuid, ownerUuid, dotPath);
    }
    pathMap.set(dotPath, referencedUuid);

    // Inverted map: referencedUuid → (ownerUuid → Set<dotPath>)
    let ownerMap = this.danglingInverted.get(referencedUuid);
    if (!ownerMap) {
      ownerMap = new Map();
      this.danglingInverted.set(referencedUuid, ownerMap);
    }
    let pathSet = ownerMap.get(ownerUuid);
    if (!pathSet) {
      pathSet = new Set();
      ownerMap.set(ownerUuid, pathSet);
    }
    pathSet.add(dotPath);
  }

  /** Remove a single entry from the inverted index. */
  private removeFromInverted(referencedUuid: string, ownerUuid: string, dotPath: string): void {
    const ownerMap = this.danglingInverted.get(referencedUuid);
    if (!ownerMap) return;
    const pathSet = ownerMap.get(ownerUuid);
    if (pathSet) {
      pathSet.delete(dotPath);
      if (pathSet.size === 0) ownerMap.delete(ownerUuid);
    }
    if (ownerMap.size === 0) this.danglingInverted.delete(referencedUuid);
  }

  /**
   * Remove all dangling references where `ownerUuid` is the holder.
   * Called when an owner actor is destroyed so stale inverted-index entries are cleaned up.
   */
  private removeDanglingRefsForOwner(ownerUuid: string): void {
    const pathMap = this.danglingForward.get(ownerUuid);
    if (!pathMap) return;
    for (const [dotPath, referencedUuid] of pathMap) {
      this.removeFromInverted(referencedUuid, ownerUuid, dotPath);
    }
    this.danglingForward.delete(ownerUuid);
  }

  /**
   * Resolve all pending actor references that were waiting for `newActorUuid` to spawn.
   * Called immediately after each actor is added to the world.
   * Only the entries in the inverted index for `newActorUuid` are visited — O(pending refs).
   */
  private resolveDanglingReferences(newActorUuid: string): void {
    const ownerMap = this.danglingInverted.get(newActorUuid);
    if (!ownerMap) return;

    const referencedActor = this.world.getActorByUuid(newActorUuid);

    for (const [ownerUuid, dotPaths] of [...ownerMap]) {
      const ownerActor = this.world.getActorByUuid(ownerUuid);
      if (!ownerActor) {
        // Owner was destroyed before its reference could be resolved — clean up forward map.
        this.danglingForward.delete(ownerUuid);
        ownerMap.delete(ownerUuid);
        continue;
      }
      if (!referencedActor) continue; // shouldn't happen; defensive guard

      // Ordered list of (host, callbackName) pairs to fire after all assignments.
      // Using an array + seen Set preserves depth-first order while deduplicating
      // (e.g. multiple resolved elements of the same array fire onRepArr only once,
      // and only after all elements have been written).
      const pendingCallbacks: [any, string][] = [];
      const seenCallbacks = new Set<string>(); // `${ownerUuid}:${dotPath-to-host}.cbName`

      const scheduleCallback = (host: any, cbName: string, dedupeKey: string): void => {
        if (seenCallbacks.has(dedupeKey)) return;
        seenCallbacks.add(dedupeKey);
        if (typeof host[cbName] === 'function') pendingCallbacks.push([host, cbName]);
      };

      const resolveSegment = (h: any, seg: string): any => {
        const idxMatch = /^\[(\d+)\]$/.exec(seg);
        return idxMatch ? h?.[parseInt(idxMatch[1], 10)] : h?.[seg];
      };

      for (const dotPath of [...dotPaths]) {
        const parts = dotPath.split('.');
        const leafPart = parts[parts.length - 1];

        // Traverse from the owner actor to the direct parent of the leaf property.
        // Dot paths for component properties start with "$components.{uuid}"; resolve
        // those two segments via getComponents() rather than a plain property access.
        // Array index segments have the form "[n]" and are resolved with a numeric key.
        const ancestorHosts: [any, string][] = [];
        let host: any = ownerActor;
        let startIndex = 0;
        let traversalOk = true;

        if (parts[0] === REPLICATION_COMPONENTS_KEY && parts.length >= 3) {
          const componentUuid = parts[1];
          const component = ownerActor
            .getComponents(SceneComponent)
            .find(c => c.uuid === componentUuid);
          if (!component) {
            traversalOk = false;
          } else {
            ancestorHosts.push([ownerActor, parts[0]], [ownerActor, parts[1]]);
            host = component;
            startIndex = 2;
          }
        }

        for (let i = startIndex; traversalOk && i < parts.length - 1; i++) {
          ancestorHosts.push([host, parts[i]]);
          host = resolveSegment(host, parts[i]);
          if (host == null) traversalOk = false;
        }
        if (!traversalOk) continue;

        // Assign the resolved actor to the leaf. Array index leaves write to host[n].
        const idxMatch = /^\[(\d+)\]$/.exec(leafPart);
        if (idxMatch) {
          host[parseInt(idxMatch[1], 10)] = referencedActor;
        } else {
          host[leafPart] = referencedActor;
        }

        // Schedule onRep callbacks depth-first (leaf first, then ancestors outward).
        // For array index leaves the callback is promoted to the parent array property
        // so onRepArr fires once after all elements are assigned, not per element.
        const callbackStartIndex = idxMatch ? ancestorHosts.length - 1 : ancestorHosts.length;
        if (!idxMatch) {
          const cbName = `onRep${leafPart.charAt(0).toUpperCase()}${leafPart.slice(1)}`;
          scheduleCallback(host, cbName, `${ownerUuid}:${dotPath}:${cbName}`);
        }
        for (let i = callbackStartIndex - 1; i >= 0; i--) {
          const [ah, prop] = ancestorHosts[i];
          const cbName = `onRep${prop.charAt(0).toUpperCase()}${prop.slice(1)}`;
          // Build a stable dedupe key from the host's dot path prefix.
          const hostPath = parts.slice(0, i + 1).join('.');
          scheduleCallback(ah, cbName, `${ownerUuid}:${hostPath}:${cbName}`);
        }

        // Remove the resolved entry from both maps.
        dotPaths.delete(dotPath);
        const forwardPaths = this.danglingForward.get(ownerUuid);
        if (forwardPaths) {
          forwardPaths.delete(dotPath);
          if (forwardPaths.size === 0) this.danglingForward.delete(ownerUuid);
        }

        console.debug(`[ClientNetWorld] Resolved dangling reference: ${ownerActor.constructor.name}.${dotPath} -> ${referencedActor.constructor.name}`);
      }

      // Fire all collected callbacks now that every actor reference has been written.
      for (const [host, cbName] of pendingCallbacks) host[cbName]();

      if (dotPaths.size === 0) ownerMap.delete(ownerUuid);
    }

    if (ownerMap.size === 0) this.danglingInverted.delete(newActorUuid);
  }

  public override clearNetObjects(): void {
    super.clearNetObjects();
    this.transportIdToActor.clear();
    this.danglingForward.clear();
    this.danglingInverted.clear();
  }

  protected override doEndPlay(): void {
    this.transportIdToActor.clear();
    this.danglingForward.clear();
    this.danglingInverted.clear();
    super.doEndPlay();
  }
}
